import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function MiltonKeynesCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Milton Keynes Pothole Claims | 26% of Estate Roads RED | Fixtyer"
        description="Milton Keynes City Council's own DfT report shows 26% of unclassified roads in RED condition — five times the 2020/21 level — while grid roads slipped during a contract change. See what the AMBER rating means for your pothole claim."
        canonical="/councils/milton-keynes"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Milton Keynes', url: '/councils/milton-keynes' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Milton Keynes earns GREEN on spend — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because projected 2025/26 capital spend of £21.1m exceeds the £10.7m DfT allocation. But the overall and condition ratings are AMBER. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. A well-funded council can still owe compensation if it failed to act on a known or inspectable defect.',
          },
          {
            question: 'What if my pothole was on a grid road or roundabout?',
            answer: 'Most grid roads are classed as B and C roads — 315km of the 1,493km network. In 2024/25, RED-condition B and C roads rose to 2.9% and green-condition share fell from 83.4% to 77.0%. The council is investing up to £90m in junction upgrades on the grid network, including Monkston, Brinklow, Crownhill and Loughton roundabouts. If your incident was on a grid route where condition was declining or major works were deferred, that context is relevant to whether inspection intervals were reasonable.',
          },
          {
            question: 'What if my pothole was on an estate or rural unclassified road?',
            answer: 'Unclassified roads make up 1,060km — 71% of Milton Keynes\' carriageway network. RED condition rose from 5.3% in 2020/21 to 26.0% in 2023/24 and 2024/25 — roughly 276km of estate and rural routes in the worst category. The council surveys only 25% of U-roads each year using Coarse Visual Inspection, with overall condition totalled over a four-year cycle. Your claim should focus on when your road was last surveyed, prior reports, and whether a rolling sample was adequate for a defect of your pothole\'s size and age.',
          },
          {
            question: 'Pothole repairs halved in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. The council repaired the equivalent of 11,648 potholes in 2024/25 — down from 26,200 in 2023/24. Milton Keynes itself explains this as a transitional year during procurement of a new highways contract, when routine repairs were reduced to safety-critical work only and planned surface treatments were deferred to 2025/26. The council still expects to repair the equivalent of around 19,500 potholes in an average year.',
          },
          {
            question: 'Does the contract transition admission help my claim?',
            answer: 'Potentially, especially for incidents in 2024/25. The council states it "experienced a drop in overall road condition" that was "largely linked to the procurement of a new highways contract", with SCANNER surveys paused and carried out out of season, and major junction upgrades deferred. That is documented knowledge that normal maintenance was interrupted — relevant when assessing whether the council had a reasonable system for knowing and fixing defects on your road.',
          },
          {
            question: 'How do I report a pothole to Milton Keynes City Council?',
            answer: 'Use the Report It section at milton-keynes.gov.uk or the dedicated potholes page. The council states it inspects every pothole reported and carries out monthly safety inspections on major routes. Provide the street name, postcode and photos if possible. For emergency safety issues call 01908 252353 (office hours) or 01908 226699 out of hours. Reporting creates a dated council record — useful evidence if the defect was known before your incident.',
          },
          {
            question: 'Does the £90m grid junction programme affect my claim?',
            answer: 'It provides context rather than a direct answer. The council acknowledges several grid road sections continued to deteriorate when junction upgrades planned for 2024/25 were paused to coincide with the new contract. Work has now commenced on the £90m programme, funded by developer tariffs, to be delivered by 2030. If your incident was on a deferred junction approach where the council knew deterioration was continuing, that strengthens arguments about reasonable inspection and repair.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Milton Keynes City Council"
        councilSlug="milton-keynes"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/milton-keynes"
        headline="Milton Keynes Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Milton Keynes</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Milton Keynes: one in four estate roads now RED
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Milton Keynes earns <strong className="text-emerald-400">GREEN on spend</strong> — projected capital
            investment nearly doubles its DfT allocation — yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> because{' '}
            <strong className="text-rose-400">26% of unclassified roads are in RED condition</strong>, up from
            just 5.3% in 2020/21. On the famous grid network, green-condition B and C roads fell to 77.0% in
            2024/25 as a contract change paused preventative work and halved pothole repairs.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">26%</div>
            <div className="text-white font-medium mt-2">Unclassified roads in RED condition (2024/25)</div>
            <div className="text-brand-300 text-sm mt-1">
              On 1,060km of estate and rural routes — roughly 276km in the worst category. The council surveys
              only 25% of this network each year, with condition totalled over a four-year cycle.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and CVI survey data from Milton Keynes City Council&apos;s own DfT transparency
            report — main roads slipped in 2024/25, estate roads in sustained decline
          </p>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (118km — 8% of network): sharp 2024/25 reversal</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">3.3%</div>
                <div className="text-sm text-rose-800">RED (2024/25)</div>
                <div className="text-xs text-surface-500">up from 1.4% in 2023/24</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">25.1%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 12.7%</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">71.6%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 85.8%</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              A-roads including routes like Monks Way (H3 — the A422) are surveyed in full every year. The council
              notes SCANNER surveys were paused and carried out out of season in 2024/25, when the network is
              naturally in poorer condition — which it says negatively affected classified-road results.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">
              B and C roads — including most grid roads (315km — 21% of network): slipping
            </h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">1.1%</td>
                    <td className="px-4 py-2 text-center">14.3%</td>
                    <td className="px-4 py-2 text-center">84.6%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">1.8%</td>
                    <td className="px-4 py-2 text-center">16.2%</td>
                    <td className="px-4 py-2 text-center">82.0%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">1.9%</td>
                    <td className="px-4 py-2 text-center">16.0%</td>
                    <td className="px-4 py-2 text-center">82.1%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">1.9%</td>
                    <td className="px-4 py-2 text-center">14.7%</td>
                    <td className="px-4 py-2 text-center">83.4%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">2.9%</td>
                    <td className="px-4 py-2 text-center font-bold">20.1%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-700">77.0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Grid roads and other B and C routes lost six percentage points of green-condition share in a single
              year. Nearly one in four kilometres on this network now needs — or will soon need — maintenance.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The DfT scorecards — GREEN spend, AMBER everywhere else</h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3">
                <RatingBadge rating="amber" size="md" />
                <span className="text-brand-200 text-sm">Overall — AMBER</span>
              </div>
              <div className="flex items-center gap-3">
                <RatingBadge rating="amber" size="md" />
                <span className="text-brand-200 text-sm">Condition — AMBER</span>
              </div>
              <div className="flex items-center gap-3">
                <RatingBadge rating="green" size="md" />
                <span className="text-brand-200 text-sm">Spend — GREEN</span>
              </div>
              <div className="flex items-center gap-3">
                <RatingBadge rating="amber" size="md" />
                <span className="text-brand-200 text-sm">Best practice — AMBER</span>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              The government rates Milton Keynes&apos; investment as strong — projected 2025/26 capital spend of{' '}
              <span className="text-emerald-400">£21.1m</span> against a DfT allocation of{' '}
              <span className="text-emerald-400">£10.7m</span> — but condition remains{' '}
              <span className="text-amber-400">AMBER</span> because the road surface data tells a different story.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 1,060km estate road problem
          </h2>
          <p className="text-surface-600 mb-8">
            71% of Milton Keynes&apos; carriageway network is unclassified — surveyed on a rolling 25% sample each year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">5.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">7.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20.9%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26.0%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26.0%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The four-year CVI cycle</h3>
              <p className="text-surface-600 text-sm mb-3">
                Milton Keynes surveys 25% of unclassified roads each year using Coarse Visual Inspection. The overall
                condition figure is totalled over the four-year survey period — so any given estate street may go
                several years between condition assessments.
              </p>
              <p className="text-surface-600 text-sm">
                The council also notes U-road condition fluctuates on a five-year surface-treatment cycle, with
                additional funding applied specifically for preventative treatments. Even so, RED share has risen
                fivefold since 2020/21 and has plateaued at 26% for two consecutive years.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">What 26% means in kilometres</h3>
              <p className="text-rose-800 text-sm mb-3">
                At 26% RED on 1,060km of unclassified roads, roughly <strong>276km</strong> of estate streets,
                cul-de-sacs and rural lanes sit in the category the council uses for roads that should be
                considered for maintenance in the next cycle.
              </p>
              <p className="text-rose-800 text-sm">
                A RED section is not necessarily an immediate safety defect — those are picked up by regular
                safety inspections — but it is a section the council&apos;s own survey data flags for treatment.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing the
              condition of its roads. For Milton Keynes&apos; unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last included in the 25% CVI sample — and what did it show?</li>
              <li>• If 26% of U-roads are RED at the last published survey, what was done about yours?</li>
              <li>• How does a four-year rolling total capture deterioration on a road surveyed only once in that window?</li>
              <li>• Did the 2024/25 contract transition reduce routine repairs on your route to safety-critical only?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of a network it samples incrementally — while publicly
              acknowledging that condition dropped during a service disruption.
            </p>
          </div>
        </div>
      </section>

      {/* Grid roads and roundabouts */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Grid roads, roundabouts and the £90m junction programme
          </h2>
          <p className="text-surface-600 mb-8">
            Milton Keynes&apos; distinctive H-road grid includes most B and C carriageways — and some of its busiest roundabouts
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="We are investing up to £90m into improving key junctions on the grid road network so Milton Keynes can handle future traffic levels. We have identified several roundabouts where we need to make some improvements now to increase capacity and reduce congestion ahead of the growth expected up to 2045."
              source="Milton Keynes City Council — Highways Maintenance Interim Report for the DfT, June 2025"
            />
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Priority junction</th>
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Location</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">Monkston roundabout</td>
                  <td className="px-6 py-4 text-surface-600">A421 and H7 Chaffron Way — first to be improved</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">Brinklow roundabout</td>
                  <td className="px-6 py-4 text-surface-600">A421 and H7 Chaffron Way — first to be improved</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">Crownhill</td>
                  <td className="px-6 py-4 text-surface-600">Tariff-funded scheme under assessment</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">Elfield Park</td>
                  <td className="px-6 py-4 text-surface-600">Tariff-funded scheme under assessment</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">Loughton</td>
                  <td className="px-6 py-4 text-surface-600">Tariff-funded scheme under assessment</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Kingston</td>
                  <td className="px-6 py-4 text-surface-600">Tariff-funded scheme under assessment</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Deferred works, continuing deterioration</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council states several grid road sections scheduled for major junction upgrades were originally
                planned for 2024/25 but paused to coincide with the new highways contract. Those sections
                &ldquo;continued to deteriorate and only received minor repairs&rdquo; until work commenced on the
                £90m programme — to be delivered by 2030, funded by developer tariffs.
              </p>
              <p className="text-surface-600 text-sm">
                Planned improvements include lane widening, enlarged roundabouts, signalisation and enhanced
                road markings — with innovation through artificial intelligence cited in the report.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions worth asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your incident on a grid approach where junction upgrades were deferred?</li>
                <li>• Did the council increase inspection frequency on routes it knew were deteriorating?</li>
                <li>• If only safety-critical repairs were carried out in 2024/25, why wasn&apos;t your defect caught?</li>
                <li>• Was the pothole on a Redway (341km of cycle paths) or footway (925km) rather than a carriageway?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            97,708 equivalent potholes in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Milton Keynes measures repairs by surface area, not individual potholes — then converts to an equivalent count
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Surface area repaired (m²)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Equivalent potholes</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">5,434</td>
                  <td className="px-6 py-4 text-center font-bold">21,736</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">4,695</td>
                  <td className="px-6 py-4 text-center font-bold">18,780</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">4,836</td>
                  <td className="px-6 py-4 text-center font-bold">19,344</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6,550</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26,200</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,912</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11,648</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold">24,427</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">97,708</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The equivalent-pothole calculation</h3>
              <p className="text-surface-600 text-sm mb-3">
                Milton Keynes defines a pothole as at least 30cm across. Each repair allows 10cm either side, so
                the minimum patch is 50cm × 50cm (0.25m²). Every square metre repaired equals four equivalent
                potholes — the basis for the table above.
              </p>
              <p className="text-surface-600 text-sm">
                The council states that in an average year it will repair the equivalent of around{' '}
                <strong>19,500 potholes</strong> — roughly 53 per day across the 1,493km road network.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why 2024/25 halved</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council explains 2024/25 was a transitional year during procurement of a new long-term
                maintenance contract. Normal operational planning was interrupted, planned surface treatments
                were deferred to 2025/26, and routine road repairs were reduced to safety-critical work only.
              </p>
              <p className="text-amber-800 text-sm">
                That is not evidence the network improved — it is evidence fewer defects were treated while
                condition data was simultaneously worsening.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="2024/25 was a transitional year as we began a new long-term maintenance contract. The process was both extensive and complex, including awarding the contract, 'demobilising' the existing arrangements, and mobilising the new contract during the service year. As a result the normal operational planning and delivery of services was interrupted."
            source="Milton Keynes City Council — Highways Maintenance Interim Report for the DfT, June 2025"
          />
        </div>
      </section>

      {/* Spend section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN spend on a £7.1bn network
          </h2>
          <p className="text-surface-600 mb-8">
            Milton Keynes describes its highway network as the city&apos;s most valuable asset — and invests accordingly
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£9.3m</td>
                  <td className="px-4 py-3 text-center">£14.1m</td>
                  <td className="px-4 py-3 text-center">£5.3m</td>
                  <td className="px-4 py-3 text-center">77%</td>
                  <td className="px-4 py-3 text-center">13%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£7.9m</td>
                  <td className="px-4 py-3 text-center">£11.8m</td>
                  <td className="px-4 py-3 text-center">£6.6m</td>
                  <td className="px-4 py-3 text-center">70%</td>
                  <td className="px-4 py-3 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£7.9m</td>
                  <td className="px-4 py-3 text-center">£11.7m</td>
                  <td className="px-4 py-3 text-center">£9.0m</td>
                  <td className="px-4 py-3 text-center">63%</td>
                  <td className="px-4 py-3 text-center">20%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£9.9m</td>
                  <td className="px-4 py-3 text-center">£17.8m</td>
                  <td className="px-4 py-3 text-center">£8.6m</td>
                  <td className="px-4 py-3 text-center">72%</td>
                  <td className="px-4 py-3 text-center">15%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£8.7m</td>
                  <td className="px-4 py-3 text-center">£12.3m</td>
                  <td className="px-4 py-3 text-center">£8.7m</td>
                  <td className="px-4 py-3 text-center">65%</td>
                  <td className="px-4 py-3 text-center">19%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26*</td>
                  <td className="px-4 py-3 text-center font-bold">£10.7m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£21.1m</td>
                  <td className="px-4 py-3 text-center font-bold">£7.4m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">77%</td>
                  <td className="px-4 py-3 text-center font-bold">9%</td>
                </tr>
              </tbody>
            </table>
            <p className="text-xs text-surface-500 px-4 py-2 bg-surface-50">* projected spend</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-brand-900">£28.5m</div>
              <div className="text-sm text-surface-600 mt-1">Total highways budget 2025/26</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-emerald-600">39km</div>
              <div className="text-sm text-surface-600 mt-1">Carriageway surface improvements planned 2025/26</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-brand-900">81km</div>
              <div className="text-sm text-surface-600 mt-1">Resurfaced or surface-treated over five years (28km + 53km)</div>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Investment without recovery — yet</h3>
            <p className="text-brand-200 mb-4">
              Over five years Milton Keynes fully resurfaced 28km (just under 2% of the network) and applied
              surface treatments to a further 53km (around 3.5%). The council adopted prudential borrowing to
              fund larger preventative programmes — yet U-road RED condition still quintupled and 2024/25
              brought the steepest classified-road decline in the published data.
            </p>
            <p className="text-white font-medium">
              GREEN spend scorecards measure whether a council invests adequately. They do not mean every road
              — or every pothole — was reasonably maintained when your incident occurred.
            </p>
          </div>
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a GREEN-spend, AMBER-condition council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Milton Keynes invests seriously — here is how that shapes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — projected capital nearly double the DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management strategy and AMX system
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Monthly safety inspections on major routes; every reported pothole inspected
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 77% of 2025/26 budget projected as preventative maintenance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> New Ringway contract with extended quality guarantees from September 2024
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on well-maintained grid routes with recent inspection records.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 26% of U-roads RED, grid roads slipping in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only 25% of U-roads CVI-surveyed each year on a four-year rolling total
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 97,708 equivalent pothole repairs in five years — defects form between inspections
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Admitted service disruption and deferred treatments during contract change
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Junction upgrades deferred while grid sections &ldquo;continued to deteriorate&rdquo;
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend scorecards, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, Report It) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road class — on a U-road, the 25% CVI sample and four-year cycle is your strongest structural argument</li>
              <li>• Location on the grid network — especially if junction upgrades were deferred in 2024/25</li>
              <li>• Timing — incidents during the contract transition when only safety-critical repairs were made</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Milton Keynes&apos; own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Milton Keynes first
          </h2>
          <p className="text-surface-600 mb-6">
            Milton Keynes City Council states it inspects every pothole reported and carries out monthly safety
            inspections on major routes and grid roads. Use the Report It section with the street name, postcode
            and photos. Potholes are assessed against the council&apos;s Code of Practice — a defect must measure
            at least 30cm across to qualify. For emergency safety issues call 01908 252353 (office hours) or
            01908 226699 out of hours.
          </p>
          <a
            href="https://www.milton-keynes.gov.uk/highways/potholes"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Milton Keynes City Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Keep your reference number and any confirmation. If the defect is on the M1, report it to National
            Highways instead.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Milton Keynes?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road CVI sample argument</li>
                <li>• No contract transition context</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 26% U-road RED condition documented</li>
                <li>✅ Grid road decline and deferred junction works cited</li>
                <li>✅ 97,708 equivalent repairs and 2024/25 disruption argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Milton Keynes</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=milton-keynes"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Milton Keynes claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Milton Keynes earns GREEN on spend — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because projected 2025/26 capital spend of £21.1m exceeds
                the £10.7m DfT allocation. But the overall and condition ratings are AMBER. Section 58 turns on
                whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not
                on aggregate spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a grid road or roundabout?
              </h3>
              <p className="text-surface-600 text-sm">
                Most grid roads are B and C roads — 315km of the network. In 2024/25, green-condition share fell
                from 83.4% to 77.0%. The council is investing up to £90m in junction upgrades including Monkston,
                Brinklow, Crownhill and Loughton roundabouts. If your incident was on a deferred junction approach,
                that context is relevant to whether inspection intervals were reasonable.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an estate or rural unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 1,060km — 71% of the network. RED condition rose from 5.3% in 2020/21 to
                26.0% in 2023/24 and 2024/25. The council surveys only 25% of U-roads each year, with condition
                totalled over a four-year cycle.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs halved in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The equivalent of 11,648 potholes were repaired in 2024/25 — down from 26,200 in 2023/24.
                Milton Keynes explains this as a transitional year during procurement of a new highways contract,
                when routine repairs were reduced to safety-critical work only. The council still expects around
                19,500 equivalent repairs in an average year.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the contract transition admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially, especially for 2024/25 incidents. The council states overall condition dropped and
                was &ldquo;largely linked to the procurement of a new highways contract&rdquo;, with SCANNER surveys
                carried out out of season and planned surface treatments deferred. That documents interrupted
                maintenance — relevant when assessing whether the council had a reasonable system for your road.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How do I report a pothole to Milton Keynes City Council?
              </h3>
              <p className="text-surface-600 text-sm">
                Use the Report It section at milton-keynes.gov.uk or the dedicated potholes page. Provide the
                street name, postcode and photos. For emergencies call 01908 252353 (office hours) or 01908 226699
                out of hours. Reporting creates a dated council record useful if the defect was known before your
                incident.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the £90m grid junction programme affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It provides context. The council acknowledges grid sections continued to deteriorate when junction
                upgrades planned for 2024/25 were paused. Work has now commenced on the £90m tariff-funded
                programme to be delivered by 2030. If your incident was on a deferred approach, that strengthens
                arguments about reasonable inspection and repair.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.milton-keynes.gov.uk/highways/our-policies-and-processes/highways-budget"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Milton Keynes City Council — Highways Maintenance Interim Report for the DfT (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
